import { Box, Flex, Icon, Text } from "@chakra-ui/react";
import NextLink from "next/link";
import { FC, ReactNode } from "react";

const SparklesIcon: FC<{ size: number }> = ({ size }) => (
  <Icon
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    boxSize={`${size}px`}
    color="upsell.base"
    flexShrink={0}
  >
    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.582a.5.5 0 0 1 0 .962L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0L9.937 15.5Z" />
    <path d="M20 3v4" />
    <path d="M22 5h-4" />
  </Icon>
);

type WhatsNewItemProps = {
  href?: string;
  children: ReactNode;
};

export const WhatsNewItem: FC<Readonly<WhatsNewItemProps>> = ({
  href,
  children,
}) => {
  const content = (
    <Flex
      align="start"
      gap={2}
      fontSize="md"
      color="text.secondary"
      lineHeight="1.6"
      sx={{ p: { margin: 0 } }}
    >
      <Box
        as="span"
        color="upsell.base"
        flexShrink={0}
        lineHeight="1.6"
        fontSize="md"
      >
        &bull;
      </Box>
      <Box sx={{ p: { margin: 0, fontSize: "md", lineHeight: "1.6" } }}>{children}</Box>
    </Flex>
  );

  if (href) {
    return (
      <NextLink passHref href={href}>
        <Box
          as="a"
          display="block"
          borderRadius="base"
          outline="none"
          textDecoration="none"
          _hover={{
            textDecoration: "none",
            "& > div": { color: "text.primary" },
          }}
          _focusVisible={{ boxShadow: "outline" }}
        >
          {content}
        </Box>
      </NextLink>
    );
  }

  return content;
};

type WhatsNewProps = {
  children: ReactNode;
};

export const WhatsNew: FC<Readonly<WhatsNewProps>> = ({ children }) => {
  return (
    <Flex
      border="1px solid"
      borderColor="purple.200"
      borderRadius="xl"
      bg="upsell.background"
      padding={5}
      mb={8}
      gap={3}
      align="start"
      sx={{ a: { color: "var(--theme-colors-link-hover)" } }}
    >
      <SparklesIcon size={20} />
      <Box flex="1">
        <Text
          fontFamily="heading"
          fontWeight="semibold"
          fontSize="sm"
          color="upsell.text"
          textTransform="uppercase"
          letterSpacing="wider"
          mb={2}
        >
          New in the docs
        </Text>
        <Flex direction="column" gap={1.5}>
          {children}
        </Flex>
      </Box>
    </Flex>
  );
};
